import type { CSSProperties, Ref } from 'react'
import { useEffect } from 'react'
import { runInAction } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react-lite'

import { getResponsiveValue } from '@jotta/ui/getResponsiveValue'
import { useBrandStore } from '@jotta/ui/useBrandTheme'
import { cn, px } from '@jotta/utils/css'
import { groupByColumns } from '@jotta/utils/groupByAspectRatio'

import type { CollectionRef } from '../../../api/useCollection'

import { AlbumPhotoThumb } from './AlbumPhotoThumb'

const minColumns = 2
type VisibleItem = {
  key: string
  item: string
  row: number
  col: number
}

export function getRoundedClass(params: {
  row: number
  col: number
  rows: number
  cols: number
  total: number
}) {
  const { row, col, rows, cols, total } = params

  const isTopRow = row === 0
  const isBottomRow = row === rows - 1
  const isFirstCol = col === 0

  const itemsInLastRow = total % cols === 0 ? cols : total % cols
  const itemsInThisRow = isBottomRow ? itemsInLastRow : cols
  const isLastInRow = col === itemsInThisRow - 1

  const hasRowBelow = row < rows - 1
  const nextRowLen = hasRowBelow
    ? row + 1 === rows - 1
      ? itemsInLastRow
      : cols
    : 0
  const hasBelow = hasRowBelow && nextRowLen > col

  return cn({
    'rounded-tl-[12px]': isTopRow && isFirstCol,
    'rounded-bl-[12px]': isBottomRow && isFirstCol,
    'rounded-tr-[12px]': isTopRow && isLastInRow,
    'rounded-br-[12px]':
      (isBottomRow && isLastInRow) || (isLastInRow && !hasBelow),
  })
}

export function getListStyleVars(params: {
  columns: number
  rows: number
  rowHeight: number
}) {
  const { columns, rows, rowHeight } = params
  return {
    '--columns': columns,
    '--rows': rows,
    '--row-height': px(rowHeight),
  } as CSSProperties
}

export const AlbumPhotosSmall = observer(function AlbumPhotosSmall({
  ids,
  showOwnerAvatar,
  showSimilarSearch,
  collectionRef,
  containerRef,
}: {
  ids: readonly string[]
  showOwnerAvatar?: boolean
  showSimilarSearch?: boolean
  collectionRef: CollectionRef
  containerRef?: Ref<HTMLDivElement>
}) {
  const branding = useBrandStore()

  const store = useLocalObservable(() => ({
    ids,

    setIds(nextIds: readonly string[]) {
      this.ids = nextIds
    },

    get columnWidth() {
      return getResponsiveValue(branding.currentBreakpointIndex, [130, 200])
    },

    get columns() {
      return Math.max(
        minColumns,
        Math.round(branding.contentWidthRounded / this.columnWidth),
      )
    },

    get grid() {
      return groupByColumns(this.ids, this.columns)
    },

    get rowHeight() {
      return Math.round(branding.contentWidthSafe / this.columns)
    },

    get viewportRows() {
      return Math.round(branding.viewportHeight / this.rowHeight)
    },

    get overscanRows() {
      return Math.round(this.viewportRows)
    },

    get overscan() {
      return this.overscanRows * this.columns
    },

    get numberOfVisibleRows() {
      return this.viewportRows + this.overscanRows * 2
    },

    get firstIndex() {
      if (!branding.scrollTop) {
        return 0
      }
      return Math.max(
        0,
        Math.floor((branding.scrollTop / this.rowHeight) * this.columns) -
          this.overscan,
      )
    },

    get lastIndex() {
      return Math.min(
        this.ids.length,
        this.firstIndex + this.numberOfVisibleRows * this.columns,
      )
    },

    get visibleItems() {
      return this.grid.data.slice(this.firstIndex, this.lastIndex)
    },

    get totalHeight() {
      return this.grid.rows * this.rowHeight
    },

    get toJSON() {
      const { firstIndex, lastIndex, rowHeight, columns, numberOfVisibleRows } =
        this
      return {
        firstIndex,
        lastIndex,
        rowHeight,
        columns,
        numberOfVisibleRows,
        numberOfVisibleItems: this.visibleItems.length,
      }
    },
  }))

  useEffect(() => {
    runInAction(() => {
      store.setIds(ids)
    })
  }, [store, ids])

  const columns = store.columns
  const rows = store.grid.rows
  const total = store.ids.length

  const listStyle = getListStyleVars({
    columns,
    rows,
    rowHeight: store.rowHeight,
  })

  const renderItems = (store.visibleItems as VisibleItem[]).map(p => ({
    key: p.key,
    id: p.item,
    row: p.row,
    col: p.col,
    transform: `translate(${p.col * 100}%, ${p.row * 100}%)`,
    rounded: getRoundedClass({
      row: p.row,
      col: p.col,
      rows,
      cols: columns,
      total,
    }),
  }))

  return (
    <div className="pb-20" ref={containerRef}>
      <div
        className="relative h-[var(--height)] w-full [--grid-line-width:2px] [--height:calc(var(--rows)*var(--row-height))] [--width:calc(100%/var(--columns,2))] before:pointer-events-none before:absolute before:left-0 before:top-0 before:h-[calc(100%_-_var(--row-height))] before:w-full before:bg-[linear-gradient(white_var(--grid-line-width),transparent_var(--grid-line-width),transparent_var(--row-height)),linear-gradient(90deg,white_var(--grid-line-width),transparent_var(--grid-line-width)),whitesmoke] before:bg-[length:var(--width)_var(--row-height)] before:content-['']"
        data-testid="AlbumPhotos"
        style={listStyle}
      >
        {renderItems.map(p => (
          <div
            key={p.key}
            className="absolute left-0 top-0 flex aspect-square h-[var(--row-height)] w-[var(--width)] p-px"
            data-col={p.col}
            data-row={p.row}
            style={{ transform: p.transform }}
          >
            <div
              className={cn(
                'relative flex h-full w-full overflow-hidden has-[:focus-visible]:z-10 has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-0 has-[:focus-visible]:outline-m3-primary',
                p.rounded,
              )}
            >
              <div className={cn('h-full w-full overflow-hidden', p.rounded)}>
                <AlbumPhotoThumb
                  id={p.id}
                  showOwnerAvatar={showOwnerAvatar}
                  showSimilarSearch={showSimilarSearch}
                  collectionRef={collectionRef}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
})
